import React, { useContext, useEffect, useState } from 'react';
import axios from "axios";
import { navigate, useQueryParams } from "raviger";
import { Field, Formik } from 'formik';
import { Switch } from 'formik-mui';
import {
  Form as BootstrapForm,
  ButtonGroup,
  Card,
  Col,
  OverlayTrigger,
  Row,
  Tooltip
} from 'react-bootstrap';
import { Typeahead } from 'react-bootstrap-typeahead';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowAltCircleLeft,
  faChevronLeft,
  faChevronRight,
  faInfoCircle
} from '@fortawesome/free-solid-svg-icons';
import * as Yup from 'yup';
import AccessTransferList from '../components/AccessTransferList.js';
import { DropDown, TextInput, ToggleSwitch } from '.././components/Form.js';
import ButtonSpinner from '../components/ButtonSpinner.js';
import { SystemErrorContext } from '../components/SystemError';
import { AuthContext } from "./AccountsReducer";

const UserForm = ({ id, organization }) => {

  // Identify any query param data.
  const [queryParams] = useQueryParams();
  const {
    next = '/',
  } = queryParams;

  const { dispatch, state } = useContext(AuthContext);
  const { setShowSystemError } = useContext(SystemErrorContext);

  // Determine if user is signing up for an incident.
  var is_signup = window.location.pathname.endsWith("/signup");
  var org_slug = next.split('/')[1];

  // Determine the TemporaryAccessLink ID if present.
  var temp_access_id = next.split('/').pop();

  // Regex validators.
  const phoneRegex = /^((\+\d{1,3}(-| )?\(?\d\)?(-| )?\d{1,3})|(\(?\d{2,3}\)?))(-| )?(\d{3,4})(-| )?(\d{4})(( x| ext)\d{1,5}){0,1}$/;

  const [existingUser, setExistingUser] = useState(false);
  const [existingUsers, setExistingUsers] = useState({data:{}, options:[], fetching:true});

  const initialData = {
    first_name: '',
    last_name: '',
    email: '',
    cell_phone: '',
    user_perms: false,
    incident_perms: false,
    vet_perms: false,
    vet_role: '',
    all_training: false,
    all_operational: false,
    organizations: [],
    // presets: id ? -1 : 0,
    has_usable_password: true,
    existing_user: false
  }

  const [data, setData] = useState(initialData);
  const [detectChange, setDetectChange] = useState([]);
  const [selected, setSelected] = useState([]);
  const [options, setOptions] = useState([]);
  const [allOptions, setAllOptions] = useState([]);
  const [trainingSelected, setTrainingSelected] = useState([]);
  const [trainingOptions, setTrainingOptions] = useState([]);
  const [allTrainingOptions, setAllTrainingOptions] = useState([]);
  const [detectTrainingChange, setDetectTrainingChange] = useState([]);

  function handleChange(newValue) {
    setDetectChange(newValue);
  };

  const handleTrainingChange = (newValue) => {
    setDetectTrainingChange(newValue);
  };

  // Update options for correct label.
  useEffect(() => {
    setSelected(detectChange);
    setOptions([{label: 'No Access', options: allOptions.filter(option => !detectChange.includes(option.value)),},{label: 'Access', options: allOptions.filter(option => detectChange.includes(option.value)),}]);
  }, [detectChange])

  // Update training options for correct label.
  useEffect(() => {
    setTrainingOptions([{label: 'No Access', options: allTrainingOptions.filter(option => !detectTrainingChange.includes(option.value)),},{label: 'Access', options: allTrainingOptions.filter(option => detectTrainingChange.includes(option.value)),}]);
    setTrainingSelected(detectTrainingChange);
  }, [detectTrainingChange])

  // Hook for initializing data.
  useEffect(() => {
    let unmounted = false;
    let source = axios.CancelToken.source();

    const fetchIncidentData = async (user_data={}) => {
      // Fetch Incident data.
      await axios.get('/incident/api/incident/?organization_slug=' + organization, {
        cancelToken: source.token,
      })
      .then(response => {
        if (!unmounted) {
          let options = [];
          let access_options = [];
          let no_access_options = [];
          let training_options = [];
          let access_training_options = [];
          let no_access_training_options = [];
          response.data.forEach(inc => {
            // Build incident option list.
            if (!inc.training) {
              options.push({
                value: inc.id,
                label: inc.end_time
                ? `(*) ${inc.name}`
                : inc.name
              });
            }
            else {
              training_options.push({
                value: inc.id,
                label: inc.end_time
                ? `(*) ${inc.name}`
                : inc.name
              });
            }
          });
          if (Object.keys(user_data).length) {
            no_access_options = options.filter(option => !user_data.incident_objects.map(incident => incident.id).includes(option.value));
            access_options = options.filter(option => user_data.incident_objects.map(incident => incident.id).includes(option.value));
            no_access_training_options = training_options.filter(option => !user_data.incident_objects.map(incident => incident.id).includes(option.value));
            access_training_options = training_options.filter(option => user_data.incident_objects.map(incident => incident.id).includes(option.value));
          }
          else {
            no_access_options = options;
            no_access_training_options = training_options;
          }
          setOptions([{label: 'No Access', options: no_access_options,},{label: 'Access', options: access_options,}]);
          setAllOptions(options);
          setAllTrainingOptions(training_options);
          setTrainingOptions([{label: 'No Access', options: no_access_training_options,},{label: 'Access', options: access_training_options,}]);
        }
      })
      .catch(error => {
        if (!unmounted) {
          setShowSystemError(true);
        }
      });
    };

    if (id) {
      const fetchUserData = async () => {
        // Fetch User data.
        await axios.get('/accounts/api/user/' + id + '/?organization=' + state.organization.id, {
          cancelToken: source.token,
        })
        .then(response => {
          if (!unmounted) {
            let operational = [];
            let training = []

            response.data.incident_objects.forEach(inc => {
              inc.training ? training.push(inc.id) : operational.push(inc.id);
            })
            setSelected(operational);
            setTrainingSelected(training);
            // Set phone field to be the pretty version.
            response.data['cell_phone'] = response.data['display_phone']
            setData(response.data);
            fetchIncidentData(response.data);
          }
        })
        .catch(error => {
          if (!unmounted) {
            setShowSystemError(true);
          }
        });
      };
      fetchUserData();
    }
    else if (!is_signup){
      fetchIncidentData();
    }

    if (!is_signup) {
      const fetchExistingUserData = async () => {
        // Fetch all users data.
        await axios.get('/accounts/api/user/?secure=true&exclude_organization=' + state.organization.id, {
          cancelToken: source.token,
        })
        .then(existingUsersResponse => {
          if (!unmounted) {
            let options = [];
            existingUsersResponse.data.forEach(user => {
              options.push({id: user.id, label: user.first_name + ' ' + user.last_name +' - ' + user.display_phone + ' - ' + user.email + ' - ' + (user.org_shorts.join(", "))})
            })
            setExistingUsers({data:existingUsersResponse.data, options:options, fetching:false});
          }
        })
        .catch(error => {
          if (!unmounted) {
            setShowSystemError(true);
          }
        });
      }
      fetchExistingUserData();
    }
    // If we're in a signup workflow, verify the TemporaryAccessLink is still valid.
    else {
      const verifyAccessLink = async () => {
        // Fetch all users data.
        await axios.get('/incident/api/tempaccess/' + temp_access_id + '/', {
          cancelToken: source.token,
        })
        .then(accessResponse => {
          if (!unmounted) {
            if (!accessResponse.data.is_valid) {
               navigate('/failed');
            }
          }
        })
        .catch(error => {
          if (!unmounted) {
            setShowSystemError(true);
          }
        });
      }
      verifyAccessLink();
    }

    // Cleanup.
    return () => {
      unmounted = true;
      source.cancel();
    };
  }, [id, state.organization.id]);

  return (
    <>
    {is_signup || (id && state.user.id === Number(id)) || (state.user && state.user.is_superuser) || (state.user && state.user.user_perms) ?
    <Formik
      initialValues={data}
      enableReinitialize={true}
      validationSchema={is_signup ? Yup.object({
        first_name: Yup.string()
          .max(50, 'Must be 50 characters or less')
          .required('Required'),
        last_name: Yup.string()
          .max(50, 'Must be 50 characters or less')
          .required('Required'),
        email: Yup.string()
          .max(50, 'Must be 50 characters or less')
          .required('Required')
          .test('email-check', 'Email is Not Available.', function(value) {
            if (!value) return true;
            return axios
              .get(`/accounts/api/user/email_check/`, { params: { email: value, exclude_pk: id } })
              .then((response) => {
                return response.data.valid;
              })
              .catch((err) => {
                return false;
              });
          }),
        cell_phone: Yup.string().when('existing_user', {
          is: () => existingUser === false,
          then: () => Yup.string().required('Required').matches(phoneRegex, "Phone number is not valid").min(10, "Phone number is not valid"),
          otherwise: () => Yup.string().required('Required')
        }),
        agency_id: Yup.string().nullable()
          .max(50, 'Must be 50 characters or less'),
      }) :
      Yup.object({
        existing_user: Yup.boolean(),
        first_name: Yup.string()
          .max(50, 'Must be 50 characters or less')
          .required('Required'),
        last_name: Yup.string()
          .max(50, 'Must be 50 characters or less')
          .required('Required'),
        email: Yup.string()
          .max(50, 'Must be 50 characters or less')
          .required('Required')
          .test('email-check', 'Email is Not Available.', function(value) {
            if (!value) return true;
            return axios
              .get(`/accounts/api/user/email_check/`, { params: { email: value, exclude_pk: id } })
              .then((response) => {
                return response.data.valid;
              })
              .catch((err) => {
                return false;
              });
          }),
        cell_phone: Yup.string().when('existing_user', {
          is: () => existingUser === false,
          then: () => Yup.string().required('Required').matches(phoneRegex, "Phone number is not valid").min(10, "Phone number is not valid"),
          otherwise: () => Yup.string().required('Required')
        }),
        access_expires_at: Yup.string().nullable(),
        user_perms: Yup.boolean(),
        incident_perms: Yup.boolean(),
        vet_perms: Yup.boolean(),
      })}
      onSubmit={(values, { setFieldError, setSubmitting }) => {
        if (!is_signup) {
          // Pass current organization value.
          values['organization'] = state.organization.id;
          // Combine incident fields to set incident access.
          values['incidents'] = selected.concat(trainingSelected);
        }

        setTimeout(() => {
          if (data.id) {
            // Clear out masked phone and email fields if applicable.
            if (values.cell_phone.includes('*')) {
              delete values['cell_phone']
            }
            if (values.email.includes('*')) {
              delete values['email']
            }
            axios.patch('/accounts/api/user/' + data.id + '/', values)
            .then((response) => {
              // Update state user data if the logged in user matches the one being changed.
              if (state.user.id === data.id) {
                dispatch({type: 'USER_LOADED', user: response.data });
              }

              if (state.prevLocation) {
                navigate(state.prevLocation);
              }
              else {
                navigate('/' + organization + '/manage/accounts');
              }
            })
            .catch(error => {
              setSubmitting(false);
              setShowSystemError(true);
            });
          }
          else {
            if (is_signup) {
              values['temp_access_id'] = temp_access_id;
              values['organization_slug'] = org_slug;
            }
            axios.post('/accounts/api/user/', values)
            .then(function () {
              if (is_signup) {
                navigate('/registered');
              }
              else {
                navigate('/' + organization + '/manage/accounts');
              }
            })
            .catch(error => {
              setSubmitting(false);
              setShowSystemError(true);
            });
          }
        }, 500);
      }}
    >
      {formikProps => (
        <Card border="secondary" className="mt-4 ms-auto me-auto" style={{width:"50%", maxWidth:"50%"}}>
          <Card.Header as="h5" className="ps-3"><span id="back" style={{ cursor: 'pointer' }} onClick={() => window.history.back()} className="me-3"><FontAwesomeIcon icon={faArrowAltCircleLeft} size="lg" inverse /></span>{!is_signup ? <span>{state.organization.name} - </span> : ""}{id ? "Update" : "New"} User{is_signup ? " Account" : ""}</Card.Header>
          <Card.Body>
            <BootstrapForm>
              {!is_signup && !id ? <span>
                <div className="mb-2">Use Existing User</div>
                <Typeahead
                  id="existing_user"
                  className="mb-3"
                  onChange={(values) => {
                    if (values.length) {
                      let user = existingUsers.data.filter(user => user.id === values[0].id)[0];
                      user['existing_user'] = true;
                      setData(user);
                      setExistingUser(true);
                    }
                    else {
                      setData(initialData);
                      setExistingUser(false);
                    }
                  }}
                  options={existingUsers.options}
                  placeholder={existingUsers.fetching ? "Loading..." : "Search..."}
                  disabled={existingUsers.fetching ? true : false}
                  emptyLabel="No matches found. Please fill out the form below."
                />
              </span> : ""}
              <Row className="gx-2">
                <TextInput
                  type="text"
                  label="First Name*"
                  name="first_name"
                  id="first_name"
                  xs="6"
                />
                <TextInput
                  type="text"
                  label="Last Name*"
                  name="last_name"
                  id="last_name"
                  xs="6"
                />
              </Row>
              <Row className="gx-2 mt-3">
                <TextInput
                  type="text"
                  label="Phone*"
                  name="cell_phone"
                  id="cell_phone"
                  xs="6"
                  value={formikProps.values.cell_phone || ''}
                />
                <TextInput
                  type="text"
                  label="Email Address*"
                  name="email"
                  id="email"
                  xs="6"
                  disabled={id && data.has_usable_password}
                />
              </Row>
              {state.user && (state.user.is_superuser || state.user.user_perms) ? <Row className="mt-3" style={{marginLeft:"-15px"}}>
                <Col>
                  <BootstrapForm.Label htmlFor="user_perms" className="ms-1">
                    Organization Admin
                      <OverlayTrigger
                      key={"user-perms"}
                      placement="top"
                      overlay={
                        <Tooltip id={`tooltip-user-perms`}>
                          Organization Admins can add new users, update existing users, manage access links, create new shelters/buildings/rooms, update existing shelters/buildings/rooms, and modify the Organization data.
                        </Tooltip>
                      }
                    >
                      <FontAwesomeIcon icon={faInfoCircle} className="fa-move-up ms-1" />
                    </OverlayTrigger>
                  </BootstrapForm.Label>
                  <div style={{marginTop:"-10px"}}><Field component={Switch} name="user_perms" type="checkbox" color="primary" /></div>
                </Col>
                <Col>
                  <BootstrapForm.Label htmlFor="incident_perms" className="ms-1">
                    Incident Admin
                    <OverlayTrigger
                      key={"incident-perms"}
                      placement="top"
                      overlay={
                        <Tooltip id={`tooltip-incident-perms`}>
                          Incident Admins can add new incidents and update/hide/remove existing incidents.
                        </Tooltip>
                      }
                    >
                      <FontAwesomeIcon icon={faInfoCircle} className="fa-move-up ms-1" />
                    </OverlayTrigger>
                  </BootstrapForm.Label>
                  <div style={{marginTop:"-10px"}}>
                    <Field
                      component={Switch}
                      onChange={(event) => {
                        formikProps.setFieldValue("incident_perms", !formikProps.values.incident_perms);
                        // When checked, set All Training and All Operational to True along with all Incidents.
                        if (event.target.checked) {
                          formikProps.setFieldValue("all_training", true);
                          formikProps.setFieldValue("all_operational", true);
                          setTrainingSelected(allTrainingOptions.map(option => option.value));
                          setTrainingOptions([{label: 'No Access', options: [],},{label: 'Access', options: allTrainingOptions,}]);
                          setSelected(allOptions.map(option => option.value));
                          setOptions([{label: 'No Access', options: [],},{label: 'Access', options: allOptions,}]);
                        }
                      }}
                      name="incident_perms"
                      type="checkbox"
                      color="primary"
                    />
                  </div>
                </Col>
                <Col>
                  <BootstrapForm.Label htmlFor="vet_perms" className="ms-1">
                    Vet Permissions
                      <OverlayTrigger
                      key={"vet-perms"}
                      placement="top"
                      overlay={
                        <Tooltip id={`tooltip-vet-perms`}>
                          Vet Permissions allows user to create and edit data in VetMed module.
                        </Tooltip>
                      }
                    >
                      <FontAwesomeIcon icon={faInfoCircle} className="fa-move-up ms-1" />
                    </OverlayTrigger>
                  </BootstrapForm.Label>
                  <div style={{marginTop:"-10px"}}><Field component={Switch} name="vet_perms" type="checkbox" color="primary" /></div>
                </Col>
              </Row> : ""}
              {formikProps.values.vet_perms === true ? 
              <Row className="mt-3">
                <Col xs="6">
                  <DropDown
                    label="Vet Role"
                    id="vet_role"
                    name="vet_role"
                    type="text"
                    isClearable={false}
                    options={[{value:"", label:"None"}, {value:"Vet Tech", label:"Vet Tech"}, {value:"Veterinarian", label:"Veterinarian"}]}
                    value={formikProps.values.vet_role||''}
                  />
                </Col>
              </Row> : ""}
              {state.user && (state.user.is_superuser || state.user.user_perms) ?
              <>
              <Row className="mt-3" style={{marginLeft:"-10px"}}>
                <ToggleSwitch name="all_training" id="all_training" label="All Training"
                  onChange={(event) => {
                    formikProps.setFieldValue("all_training", !formikProps.values.all_training);
                    if (event.target.checked) {
                      setTrainingSelected(allTrainingOptions.map(option => option.value));
                      setTrainingOptions([{label: 'No Access', options: [],},{label: 'Access', options: allTrainingOptions,}]);
                    }
                    else {
                      setTrainingSelected([]);
                      setTrainingOptions([{label: 'No Access', options: allTrainingOptions,},{label: 'Access', options: [],}]);
                    }
                  }}
                  disabled={formikProps.values.incident_perms}
                />
              </Row>
              <label className="mt-1 mb-1">
              Training Incident Access
              <OverlayTrigger
                key={"training-access"}
                placement="top"
                overlay={
                  <Tooltip id={`tooltip-training-access`}>
                    (*) Indicates a hidden incident.
                  </Tooltip>
                }
              >
                <FontAwesomeIcon icon={faInfoCircle} className="fa-move-up ms-1" />
              </OverlayTrigger>
              </label>
              <Row style={{marginLeft:"0px"}}>
                <AccessTransferList
                  className="ps-0"
                  canFilter
                  options={trainingOptions}
                  selected={trainingSelected}
                  onChange={(newValue) => handleTrainingChange(newValue)}
                  disabled={formikProps.values.all_training}
                  icons={{
                    moveToAvailable: <FontAwesomeIcon icon={faChevronLeft} />,
                    moveAllToAvailable: [
                      <FontAwesomeIcon key={0} icon={faChevronLeft} />,
                      <FontAwesomeIcon key={1} icon={faChevronLeft} />,
                    ],
                    moveToSelected: <FontAwesomeIcon icon={faChevronRight} />,
                    moveAllToSelected: [
                      <FontAwesomeIcon key={0} icon={faChevronRight} />,
                      <FontAwesomeIcon key={1} icon={faChevronRight} />,
                    ],
                    moveDown: <span className="fa fa-chevron-down" />,
                    moveUp: <span className="fa fa-chevron-up" />,
                    moveTop: <span className="fa fa-double-angle-up" />,
                    moveBottom: <span className="fa fa-double-angle-down" />,
                  }}
              />
              </Row>
              <Row className="mt-4" style={{marginLeft:"-10px"}}>
                <Col>
                  <ToggleSwitch name="all_operational" id="all_operational" label="All Operational"
                    onChange={(event) => {
                      formikProps.setFieldValue("all_operational", !formikProps.values.all_operational);
                      if (event.target.checked) {
                        setSelected(allOptions.map(option => option.value));
                        setOptions([{label: 'No Access', options: [],},{label: 'Access', options: allOptions,}]);
                      }
                      else {
                        setSelected([]);
                        setOptions([{label: 'No Access', options: allOptions,},{label: 'Access', options: [],}]);
                      }
                    }}
                    disabled={formikProps.values.incident_perms}
                  />
                </Col>
              </Row>
              <label className="mt-1 mb-1">
                Operational Incident Access
                <OverlayTrigger
                  key={"operational-access"}
                  placement="top"
                  overlay={
                    <Tooltip id={`tooltip-operational-access`}>
                      (*) Indicates a hidden incident.
                    </Tooltip>
                  }
                >
                  <FontAwesomeIcon icon={faInfoCircle} className="fa-move-up ms-1" />
                </OverlayTrigger>
              </label>
              <Row style={{marginLeft:"0px"}}>
                <AccessTransferList
                  className="ps-0"
                  canFilter
                  options={options}
                  selected={selected}
                  onChange={(newValue) => handleChange(newValue)}
                  disabled={formikProps.values.all_operational}
                  icons={{
                    moveToAvailable: <FontAwesomeIcon icon={faChevronLeft} />,
                    moveAllToAvailable: [
                      <FontAwesomeIcon key={0} icon={faChevronLeft} />,
                      <FontAwesomeIcon key={1} icon={faChevronLeft} />,
                    ],
                    moveToSelected: <FontAwesomeIcon icon={faChevronRight} />,
                    moveAllToSelected: [
                      <FontAwesomeIcon key={0} icon={faChevronRight} />,
                      <FontAwesomeIcon key={1} icon={faChevronRight} />,
                    ],
                    moveDown: <span className="fa fa-chevron-down" />,
                    moveUp: <span className="fa fa-chevron-up" />,
                    moveTop: <span className="fa fa-double-angle-up" />,
                    moveBottom: <span className="fa fa-double-angle-down" />,
                  }}
                />
              </Row>
              </> : ""}
            </BootstrapForm>
          </Card.Body>
          <ButtonGroup size="lg">
            <ButtonSpinner isSubmitting={formikProps.isSubmitting} isSubmittingText="Saving..." onClick={() => { formikProps.submitForm() }}>Save</ButtonSpinner>
          </ButtonGroup>
        </Card>
      )}
    </Formik> : ""}
    </>
  );
};

export default UserForm;
